import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Rocket, RefreshCw, Globe, Mail, Clock, Shield, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_OPTIONS = ['pending', 'in_progress', 'completed', 'rejected'];

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-100 text-rose-800 border-rose-200',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const GoLiveRequests = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const isAuthorized = user?.role === 'super_admin' || user?.role === 'master_admin';

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/go-live-requests');
      setRequests(res.data?.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load go-live requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) fetchRequests();
  }, []);

  const updateStatus = async (request_id, status) => {
    setUpdatingId(request_id);
    try {
      await api.put(`/admin/go-live-requests/${request_id}/status`, { status });
      setRequests((prev) => prev.map((r) => (r.request_id === request_id ? { ...r, status } : r)));
      toast.success(`Marked as ${status.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="flex" data-testid="go-live-requests-page">
        <Sidebar />
        <div className="flex-1 bg-slate-50 flex items-center justify-center h-screen">
          <Card className="w-96">
            <CardContent className="pt-6 text-center">
              <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
              <p className="text-slate-500">Only Super Admins can view go-live requests.</p>
              <Button className="mt-4" onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <div className="flex" data-testid="go-live-requests-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Rocket className="text-emerald-600" size={32} />
                Go Live Requests
              </h1>
              <p className="text-slate-600">
                Customers ready to launch.{' '}
                {pendingCount > 0 && (
                  <span className="font-semibold text-amber-700">{pendingCount} pending review.</span>
                )}
              </p>
            </div>
            <Button variant="outline" onClick={fetchRequests} disabled={loading} data-testid="refresh-go-live-button">
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-400">Loading requests…</div>
          ) : requests.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No go-live requests yet.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {requests.map((r) => (
                <Card key={r.request_id} data-testid={`go-live-request-${r.request_id}`} className="overflow-hidden">
                  <CardContent className="p-5">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900 truncate">{r.tenant_name || 'Unknown Tenant'}</h3>
                          <Badge variant="outline" className={`capitalize ${STATUS_STYLES[r.status] || ''}`}>
                            {(r.status || 'pending').replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                          <span className="flex items-center gap-2"><Mail size={14} className="text-slate-400" /> {r.requested_by || '—'}</span>
                          <span className="flex items-center gap-2"><Clock size={14} className="text-slate-400" /> {formatDate(r.created_at)}</span>
                        </div>
                        {Array.isArray(r.checklist_completed) && r.checklist_completed.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1"><Globe size={12} /> Checklist completed</p>
                            <ul className="text-sm text-slate-700 space-y-0.5">
                              {r.checklist_completed.map((item, i) => (
                                <li key={i} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500 shrink-0" /> {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.notes && (
                          <p className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-lg p-3">
                            <span className="font-medium">Notes: </span>{r.notes}
                          </p>
                        )}
                      </div>
                      <div className="shrink-0 w-full md:w-44">
                        <label className="text-xs font-semibold text-slate-500 mb-1 block">Update status</label>
                        <Select
                          value={r.status || 'pending'}
                          onValueChange={(val) => updateStatus(r.request_id, val)}
                          disabled={updatingId === r.request_id}
                        >
                          <SelectTrigger data-testid={`status-select-${r.request_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((s) => (
                              <SelectItem key={s} value={s} className="capitalize">{s.replace('_', ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoLiveRequests;
